import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useState, type ChangeEvent, type FC } from "react";

const ANY_LINK_VALUE = "__any__";

interface FollowUpLinkSelectDialogProps {
  open: boolean;
  hyperlinks: string[];
  value: string | null | undefined;
  onSelect: (url: string | null) => void;
  onCancel: () => void;
}

function toRadioValue(value: string | null | undefined): string | undefined {
  if (value === null) return ANY_LINK_VALUE;
  if (value === undefined) return undefined;
  return value;
}

const FollowUpLinkSelectDialogContent: FC<{
  hyperlinks: string[];
  initialValue: string | null | undefined;
  onSelect: (url: string | null) => void;
  onCancel: () => void;
}> = ({ hyperlinks, initialValue, onSelect, onCancel }) => {
  const [selected, setSelected] = useState<string | undefined>(toRadioValue(initialValue));

  const handleChange = useCallback((_: ChangeEvent<HTMLInputElement>, val: string): void => {
    setSelected(val);
  }, []);

  const handleSelect = useCallback((): void => {
    if (selected === undefined) return;
    onSelect(selected === ANY_LINK_VALUE ? null : selected);
  }, [selected, onSelect]);

  return (
    <>
      <DialogTitle>Select link/button for follow up</DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={2}>
          <Typography variant="body2">
            You can check for the correct link by using the &quot;View&quot; button (link will open
            in a new tab).
          </Typography>
          <RadioGroup
            name="follow-up-link-select-group"
            value={selected ?? ""}
            onChange={handleChange}
          >
            <Stack direction="column" spacing={1}>
              <FormControlLabel
                value={ANY_LINK_VALUE}
                control={<Radio size="small" />}
                label={<Typography variant="body2">Any link in this Communication</Typography>}
              />
              {hyperlinks.map((url) => (
                <Stack key={url} direction="row" alignItems="center" spacing={1}>
                  <FormControlLabel
                    value={url}
                    control={<Radio size="small" />}
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 300,
                        }}
                      >
                        {url}
                      </Typography>
                    }
                    sx={{ flex: 1, minWidth: 0 }}
                  />
                  <Button
                    data-analytics-id="follow-up-link-select-view-button"
                    variant="text"
                    size="small"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    startIcon={<OpenInNew fontSize="small" />}
                    sx={{ flexShrink: 0, fontWeight: 600 }}
                  >
                    View
                  </Button>
                </Stack>
              ))}
            </Stack>
          </RadioGroup>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "flex-end", px: 3, pb: 2 }}>
        <Button
          data-analytics-id="follow-up-link-select-cancel-button"
          variant="text"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          data-analytics-id="follow-up-link-select-confirm-button"
          variant="contained"
          onClick={handleSelect}
          disabled={selected === undefined}
        >
          Select
        </Button>
      </DialogActions>
    </>
  );
};

export const FollowUpLinkSelectDialog: FC<FollowUpLinkSelectDialogProps> = ({
  open,
  hyperlinks,
  value,
  onSelect,
  onCancel,
}) => (
  <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
    {open && (
      <FollowUpLinkSelectDialogContent
        hyperlinks={hyperlinks}
        initialValue={value}
        onSelect={onSelect}
        onCancel={onCancel}
      />
    )}
  </Dialog>
);
